import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/i18n";

const SECTIONS = [
  {
    id: "titolare",
    title: "1. Titolare del trattamento",
    body: (
      <>
        <p>
          Il titolare del trattamento dei dati personali è <strong>Seamatch</strong>,
          contattabile all'indirizzo email <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a>.
        </p>
        <p>
          La presente informativa è resa ai sensi del Regolamento UE 2016/679 (GDPR) e descrive le modalità
          di raccolta e trattamento dei dati personali degli utenti che interagiscono con il sito
          <a href="https://seamatch.it" className="text-navy underline mx-1" target="_blank" rel="noreferrer">seamatch.it</a>
          e con i servizi correlati.
        </p>
      </>
    ),
  },
  {
    id: "dati",
    title: "2. Tipologie di dati raccolti",
    body: (
      <>
        <p>Raccogliamo esclusivamente i dati strettamente necessari per fornirti il servizio richiesto:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Dati di contatto</strong>: nome, cognome, indirizzo email, numero di telefono.</li>
          <li><strong>Dati relativi alla richiesta di viaggio</strong>: budget indicativo, numero di partecipanti, tipologia di gruppo, destinazioni di interesse, periodo, tipologia di imbarcazione, esperienza nautica.</li>
          <li><strong>Dati tecnici di navigazione</strong>: indirizzo IP, tipo di browser, dispositivo, pagine visitate (raccolti in forma anonima/aggregata per finalità statistiche).</li>
        </ul>
      </>
    ),
  },
  {
    id: "finalita",
    title: "3. Finalità e base giuridica del trattamento",
    body: (
      <>
        <p>I dati personali raccolti vengono trattati per le seguenti finalità:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Erogazione del servizio</strong> di intermediazione e consulenza nautica: analisi della richiesta, contatto con le società charter, formulazione delle proposte personalizzate. Base giuridica: esecuzione di un contratto/misure precontrattuali (art. 6.1.b GDPR).</li>
          <li><strong>Adempimento di obblighi di legge</strong>, ad esempio fiscali, contabili e di sicurezza informatica. Base giuridica: obbligo legale (art. 6.1.c GDPR).</li>
          <li><strong>Comunicazioni di marketing</strong> (newsletter, aggiornamenti su nuove destinazioni, offerte): solo previo tuo consenso esplicito e revocabile in qualsiasi momento. Base giuridica: consenso (art. 6.1.a GDPR).</li>
        </ul>
      </>
    ),
  },
  {
    id: "condivisione",
    title: "4. Condivisione dei dati con terze parti",
    body: (
      <>
        <p>
          Per fornirti le proposte richieste, alcuni tuoi dati possono essere condivisi con le società charter
          partner di Seamatch, esclusivamente nei limiti necessari alla formulazione del preventivo
          (tipicamente: nome, contatto e dettagli del viaggio richiesto). Le società charter agiscono come autonomi titolari del trattamento.
        </p>
        <p>
          Utilizziamo inoltre fornitori tecnici (hosting, invio email transazionali, analytics)
          che agiscono come responsabili del trattamento e sono vincolati da accordi di riservatezza ai sensi del GDPR.
          Non vendiamo né cediamo i tuoi dati per finalità commerciali di terze parti.
        </p>
      </>
    ),
  },
  {
    id: "conservazione",
    title: "5. Periodo di conservazione",
    body: (
      <>
        <p>
          I dati relativi alle richieste di viaggio sono conservati per un periodo massimo di <strong>24 mesi</strong> dal primo contatto,
          per consentire eventuali rinnovi del servizio e gestire la relazione commerciale.
        </p>
        <p>
          I dati raccolti per finalità di marketing sono conservati fino alla revoca del consenso.
          I dati richiesti per obblighi di legge (fiscali, contabili) sono conservati per i termini previsti dalla normativa applicabile.
        </p>
      </>
    ),
  },
  {
    id: "diritti",
    title: "6. I tuoi diritti",
    body: (
      <>
        <p>In qualità di interessato, puoi esercitare in qualunque momento i seguenti diritti:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Accesso ai tuoi dati personali (art. 15 GDPR)</li>
          <li>Rettifica dei dati inesatti o incompleti (art. 16 GDPR)</li>
          <li>Cancellazione dei dati - diritto all'oblio (art. 17 GDPR)</li>
          <li>Limitazione del trattamento (art. 18 GDPR)</li>
          <li>Portabilità dei dati (art. 20 GDPR)</li>
          <li>Opposizione al trattamento (art. 21 GDPR)</li>
          <li>Revoca del consenso in ogni momento, senza pregiudicare la liceità del trattamento basata sul consenso prestato in precedenza</li>
        </ul>
        <p>
          Per esercitare uno qualsiasi di questi diritti scrivi a
          <a href="mailto:info@seamatch.pro" className="text-navy underline mx-1">info@seamatch.pro</a>.
          Riceverai una risposta entro 30 giorni dalla ricezione della richiesta.
        </p>
        <p>
          Hai inoltre il diritto di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali
          (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-navy underline">www.garanteprivacy.it</a>)
          qualora ritenga che il trattamento dei tuoi dati violi la normativa applicabile.
        </p>
      </>
    ),
  },
  {
    id: "cookie",
    title: "7. Cookie e tecnologie simili",
    body: (
      <>
        <p>
          Il sito utilizza esclusivamente cookie tecnici necessari al funzionamento (gestione della sessione, preferenze di lingua).
          Non utilizziamo cookie di profilazione né di terze parti per finalità di marketing.
        </p>
      </>
    ),
  },
  {
    id: "modifiche",
    title: "8. Modifiche alla presente informativa",
    body: (
      <>
        <p>
          Seamatch si riserva il diritto di aggiornare la presente informativa per riflettere modifiche normative o operative.
          La versione vigente è sempre disponibile su questa pagina. L'ultima revisione è indicata in fondo.
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  const { lang, localizePath } = useI18n();
  const EN = lang === "en";
  return (
    <div data-testid="privacy-policy-page" className="bg-white">
      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sand via-white to-white" />
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <Link to={localizePath("/")} data-testid="privacy-back" className="inline-flex items-center text-sm text-ink/60 hover:text-navy transition">
            <ArrowLeft className="h-4 w-4 mr-1" /> Home
          </Link>
          {EN && (
            <div className="mt-6 p-4 rounded-xl bg-sand border border-[#E5E1D2]">
              <p className="text-xs uppercase tracking-wider text-gold font-semibold">English summary</p>
              <p className="mt-2 text-sm text-ink leading-relaxed">
                The full privacy notice is published in Italian, as required by Seamatch's regulatory framework (EU GDPR 2016/679, Italian data protection authority).
                In short: we only collect the data needed to handle your request (name, email, phone, trip preferences) and we never share them with third parties for marketing.
                For an English-language extract or any specific question, please write to <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> — we reply within 30 days, as required by GDPR.
              </p>
            </div>
          )}
          <p className="label-eyebrow mt-6">Informativa</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-navy tracking-tight leading-[1.05]">
            Privacy
            <br />
            <em className="italic text-gold font-medium">Policy.</em>
          </h1>
          <p className="mt-6 text-base text-ink leading-relaxed max-w-2xl">
            La tua privacy è una priorità. In questa pagina trovi in modo chiaro quali dati raccogliamo,
            come li utilizziamo e quali diritti hai sempre a disposizione, ai sensi del Regolamento UE 2016/679 (GDPR).
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <div className="space-y-12">
            {SECTIONS.map((s) => (
              <div key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`} className="scroll-mt-24">
                <h2 className="font-serif text-2xl sm:text-3xl text-navy leading-tight">{s.title}</h2>
                <div className="mt-4 space-y-3 text-ink leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 p-6 rounded-2xl bg-sand border border-[#E2E8F0]">
            <p className="font-serif text-xl text-navy">Hai domande sulla gestione dei tuoi dati?</p>
            <p className="mt-2 text-sm text-ink/70">
              Scrivici a <a href="mailto:info@seamatch.pro" className="text-navy underline">info@seamatch.pro</a> — rispondiamo entro 30 giorni come previsto dal GDPR.
            </p>
          </div>

          <p className="mt-10 text-xs text-ink/50">
            Ultimo aggiornamento: Febbraio 2026
          </p>
        </div>
      </section>
    </div>
  );
}
